<script setup lang="ts">
  import { ref, onMounted, computed } from 'vue'
  import { X, AlertCircle, AlertTriangle, CheckCircle, Info, Sparkles } from 'lucide-vue-next'
  import DOMPurify from 'dompurify'
  import { AnnouncementsService } from '@/types/generated/services.gen'
  import type { Announcement } from '@/types/generated/types.gen'

  const announcements = ref<Announcement[]>([])
  const dismissedAnnouncements = ref<Set<string>>(new Set())
  const dismissedAnnouncementsKey = 'dismissedAnnouncements'

  const visibleAnnouncements = computed(
    () =>
      announcements.value?.filter((a: Announcement) => !dismissedAnnouncements.value.has(a.uuid)) ??
      []
  )

  const sanitizeHtml = (html: string): string => {
    return DOMPurify.sanitize(html, {
      ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'a', 'p', 'br', 'ul', 'ol', 'li', 'span'],
      ALLOWED_ATTR: ['href', 'target', 'rel'],
      ALLOW_DATA_ATTR: false
    })
  }

  const loadDismissedAnnouncements = () => {
    const stored = localStorage.getItem(dismissedAnnouncementsKey)
    if (stored) {
      try {
        dismissedAnnouncements.value = new Set(JSON.parse(stored))
      } catch (error) {
        console.error('Failed to parse dismissed announcements:', error)
        dismissedAnnouncements.value = new Set()
        localStorage.removeItem(dismissedAnnouncementsKey)
      }
    }
  }

  const saveDismissedAnnouncements = () => {
    try {
      localStorage.setItem(
        dismissedAnnouncementsKey,
        JSON.stringify(Array.from(dismissedAnnouncements.value))
      )
    } catch (error) {
      console.error('Failed to save dismissed announcements:', error)
    }
  }

  const fetchAnnouncements = async () => {
    try {
      const response = await AnnouncementsService.announcementsActiveList()
      announcements.value = response
    } catch (error) {
      console.error('Failed to fetch announcements:', error)
      announcements.value = []
    }
  }

  const dismissAnnouncement = (uuid: string) => {
    dismissedAnnouncements.value.add(uuid)
    saveDismissedAnnouncements()
  }

  const getIcon = (type: string) => {
    switch (type) {
      case 'ERROR':
        return AlertCircle
      case 'WARNING':
        return AlertTriangle
      case 'SUCCESS':
        return CheckCircle
      default:
        return Info
    }
  }

  const getStyles = (type: string) => {
    switch (type) {
      case 'ERROR':
        return {
          container:
            'bg-gradient-to-r from-red-50 via-red-50/80 to-red-50/60 dark:from-red-950/40 dark:via-red-950/30 dark:to-red-950/20 border-red-200/60 dark:border-red-800/50',
          iconBg: 'bg-red-100 dark:bg-red-900/50',
          icon: 'text-red-600 dark:text-red-400',
          text: 'text-red-900 dark:text-red-100',
          button: 'hover:bg-red-200/50 dark:hover:bg-red-900/30 focus:ring-red-500'
        }
      case 'WARNING':
        return {
          container:
            'bg-gradient-to-r from-amber-50 via-amber-50/80 to-amber-50/60 dark:from-amber-950/40 dark:via-amber-950/30 dark:to-amber-950/20 border-amber-200/60 dark:border-amber-800/50',
          iconBg: 'bg-amber-100 dark:bg-amber-900/50',
          icon: 'text-amber-600 dark:text-amber-400',
          text: 'text-amber-900 dark:text-amber-100',
          button: 'hover:bg-amber-200/50 dark:hover:bg-amber-900/30 focus:ring-amber-500'
        }
      case 'SUCCESS':
        return {
          container:
            'bg-gradient-to-r from-emerald-50 via-emerald-50/80 to-emerald-50/60 dark:from-emerald-950/40 dark:via-emerald-950/30 dark:to-emerald-950/20 border-emerald-200/60 dark:border-emerald-800/50',
          iconBg: 'bg-emerald-100 dark:bg-emerald-900/50',
          icon: 'text-emerald-600 dark:text-emerald-400',
          text: 'text-emerald-900 dark:text-emerald-100',
          button: 'hover:bg-emerald-200/50 dark:hover:bg-emerald-900/30 focus:ring-emerald-500'
        }
      default:
        return {
          container:
            'bg-gradient-to-r from-blue-50 via-blue-50/80 to-blue-50/60 dark:from-blue-950/40 dark:via-blue-950/30 dark:to-blue-950/20 border-blue-200/60 dark:border-blue-800/50',
          iconBg: 'bg-blue-100 dark:bg-blue-900/50',
          icon: 'text-blue-600 dark:text-blue-400',
          text: 'text-blue-900 dark:text-blue-100',
          button: 'hover:bg-blue-200/50 dark:hover:bg-blue-900/30 focus:ring-blue-500'
        }
    }
  }

  onMounted(() => {
    loadDismissedAnnouncements()
    fetchAnnouncements()
  })
</script>

<template>
  <TransitionGroup
    name="announcement"
    tag="div"
    class="relative"
  >
    <div
      v-for="announcement in visibleAnnouncements"
      :key="announcement.uuid"
      :class="[
        'relative overflow-hidden border-b backdrop-blur-sm transition-all duration-300',
        getStyles(announcement.message_type).container
      ]"
    >
      <div
        class="pointer-events-none absolute inset-0 bg-gradient-to-r from-white/0 via-white/10 to-white/0
          dark:from-white/0 dark:via-white/5 dark:to-white/0"
      ></div>

      <div class="relative mx-auto max-w-7xl px-4 py-3.5 sm:px-6 lg:px-8">
        <div class="flex items-start gap-3">
          <div
            :class="[
              'flex-shrink-0 rounded-lg p-1.5 shadow-sm ring-1 ring-black/5 dark:ring-white/10',
              getStyles(announcement.message_type).iconBg
            ]"
          >
            <component
              :is="getIcon(announcement.message_type)"
              :class="['h-4 w-4', getStyles(announcement.message_type).icon]"
            />
          </div>

          <div class="min-w-0 flex-1 pt-0.5">
            <div
              :class="[
                'text-sm font-medium leading-relaxed',
                getStyles(announcement.message_type).text
              ]"
              v-html="sanitizeHtml(announcement.message)"
            ></div>
          </div>

          <button
            v-if="announcement.dismissible"
            :class="[
              'inline-flex flex-shrink-0 items-center justify-center rounded-lg p-1.5',
              'transition-all duration-200 ease-out',
              'focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-offset-transparent',
              'transform hover:scale-105 active:scale-95',
              getStyles(announcement.message_type).button,
              getStyles(announcement.message_type).icon
            ]"
            type="button"
            @click="dismissAnnouncement(announcement.uuid)"
          >
            <span class="sr-only">Dismiss</span>
            <X class="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  </TransitionGroup>
</template>

<style scoped>
  .announcement-enter-active {
    transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  }

  .announcement-leave-active {
    transition: all 0.3s cubic-bezier(0.4, 0, 1, 1);
  }

  .announcement-enter-from {
    opacity: 0;
    transform: translateY(-100%);
    max-height: 0;
  }

  .announcement-leave-to {
    opacity: 0;
    transform: translateX(100%);
    max-height: 0;
    margin-bottom: 0;
  }

  .announcement-enter-to,
  .announcement-leave-from {
    opacity: 1;
    transform: translateY(0) translateX(0);
    max-height: 200px;
  }

  .announcement-move {
    transition: transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  }
</style>
